import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { setIsUpdatingOrderStatus } from 'data/cacheHelpers/order';

import {
  markCompleteOrder,
  markReadyOrder,
  printOrderAction,
  Variables,
  ResponseStatus,
} from 'data/requests/orderActions';
import { CDError } from 'utils/error';

const useOrderActions = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: markReadyStatusMutation, isLoading: completeLoading } =
    useMutation<ResponseStatus, CDError, Variables>(markReadyOrder);

  const { mutateAsync: markCompleteStatusMutation, isLoading: readyLoading } =
    useMutation<ResponseStatus, CDError, Variables>(markCompleteOrder);

  const { mutateAsync: printOrderMutation, isLoading: isPrintLoading } =
    useMutation<ResponseStatus, CDError, Variables>(printOrderAction);

  const markReady = useCallback(
    async ({ orderId }: Variables) => {
      try {
        const response = await markReadyStatusMutation({
          orderId,
        });
        setIsUpdatingOrderStatus(orderId, true);
        return response;
      } catch (error) {
        if (error instanceof CDError) {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
        }
        return error;
      }
    },
    [markReadyStatusMutation, enqueueSnackbar],
  );

  const markComplete = useCallback(
    async ({ orderId }: Variables) => {
      try {
        const response = await markCompleteStatusMutation({
          orderId,
        });
        setIsUpdatingOrderStatus(orderId, true);
        return response;
      } catch (error) {
        if (error instanceof CDError) {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
        }
        return error;
      }
    },
    [markCompleteStatusMutation, enqueueSnackbar],
  );

  const printOrder = useCallback(
    async ({ orderId }: Variables) => {
      try {
        const response = await printOrderMutation({
          orderId,
        });
        enqueueSnackbar(`Order printed`, {
          variant: 'success',
        });
        return response;
      } catch (error) {
        if (error instanceof CDError) {
          enqueueSnackbar(error.message, {
            variant: 'error',
          });
        }
        return error;
      }
    },
    [printOrderMutation, enqueueSnackbar],
  );

  return {
    isLoading: completeLoading || readyLoading,
    isPrintLoading,
    markReady,
    markComplete,
    printOrder,
  };
};

export default useOrderActions;
